import { ListSubheader } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiSelect from '@material-ui/core/Select';
import groupBy from 'lodash.groupby';
import { NullableString } from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { bodyMedium, bodySmall } from '../../styles/text';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Anchor } from '../Link';
import { Tooltip } from '../Tooltip';
import { VisuallyHidden } from '../VisuallyHidden';
import { SelectGroup } from './types';

const leftRightPadding = '0.5rem';
const borderWidthInPx = 1;
const widthOfTriangle = '24px';

const SelectContainer = styled(MuiSelect)`
  height: 2rem;
  font-size: 0.875rem;
  background-color: ${({ theme }) => theme.colors.white};
  border: ${borderWidthInPx}px solid ${({ theme }) => theme.colors.grayddd};
  border-radius: 4px;

  &:not(.Mui-disabled) {
    &.Mui-focused,
    &:hover {
      border-color: ${({ theme }) => theme.colors.gray333};
    }
  }

  &.Mui-error {
    border-color: ${({ theme }) => theme.colors.darkRed};
  }

  /* child div that the menu is the anchor element for the menu */
  & .MuiSelect-root {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 ${widthOfTriangle} 0 ${leftRightPadding};
  }
`;

const SelectLabel = styled(MuiInputLabel)`
  ${bodyMedium}
  position: static;
  color: ${({ theme }) => theme.colors.gray333};
  transform: none;

  &.Mui-focused {
    color: ${({ theme }) => theme.colors.gray333};
  }

  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};

    .MuiFormLabel-asterisk {
      color: ${({ theme }) => theme.colors.darkRed};
    }
  }
`;

const MenuItem = styled(MuiMenuItem)`
  padding-right: ${leftRightPadding};
  padding-left: ${leftRightPadding};
  font-size: 0.875rem;

  &.MuiMenuItem-root:hover,
  &.MuiMenuItem-root.Mui-focusVisible {
    background-color: ${({ theme }) => theme.colors.grayddd};
  }
`;

const GroupLabel = styled(ListSubheader)`
  display: inline-flex;
  align-items: center;
  padding: 0 ${leftRightPadding};
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  line-height: unset;

  &:first-child {
    margin-top: 0.25rem;
  }
`;

const GroupTooltip = styled(Tooltip)`
  padding-right: 0.3rem;
  padding-left: 0.3rem;
  margin-left: -0.4rem;
`;

export const Value = styled.div<{
  $isPlaceholder?: boolean;
  $isString?: boolean;
}>`
  ${({ $isString }) =>
    $isString ? '' : `display: flex; align-items: center;`};
  max-width: 100%;
  overflow: hidden;
  ${({ $isPlaceholder, theme }) =>
    $isPlaceholder ? `color: ${theme.colors.gray999};` : ''};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubText = styled(FormHelperText)`
  max-width: 300px;

  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};
  }
`;

const ErrorText = styled(FormHelperText)`
  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};
  }
`;

const InputLabelContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.2rem;
`;

const LabelAndTooltipContainer = styled.div`
  display: inline-flex;
  align-items: inherit;
`;

const ExplanationLink = styled(Anchor)`
  ${bodySmall}
`;

const NoOptionsText = styled.div`
  padding: 0.5rem;
`;

export type SelectValue = React.ComponentProps<typeof MuiMenuItem>['value'];

export type BaseSelectOption = {
  value: Exclude<SelectValue, undefined>;
  subText?: string;
  disabled?: boolean;
  /** If specified, will place option under the group with a matching name */
  groupName?: string;
};

export type BaseSelectProps<OptionType extends BaseSelectOption> =
  React.ComponentProps<typeof MuiSelect> & {
    dontSaveSpaceForErrorText?: boolean;
    errorText?: string;
    isLabelHidden?: boolean;
    subText?: NullableString;
    label?: string;
    name: string;
    noOptionsText?: string;
    options: OptionType[];
    groups?: SelectGroup[];
    showBlankOption?: boolean;
    tooltipText?: string;
    explanationUrl?: string;
    isReadOnly?: boolean;
    isErrorTextHidden?: boolean;
    isCompact?: boolean;
    isSubtextHidden?: boolean;
    isEmpty?: boolean;
    renderOption: (option: OptionType) => React.ReactNode;
  } & {
    renderValue: (
      value: BaseSelectProps<OptionType>['value'],
    ) => React.ReactNode;
  };

function Placeholder({ placeholder }: { placeholder: string | undefined }) {
  if (!placeholder) return null;
  return (
    <Value $isPlaceholder $isString>
      {placeholder}
    </Value>
  );
}

export function BaseSelect<OptionType extends BaseSelectOption>({
  className,
  dontSaveSpaceForErrorText,
  error,
  errorText,
  isLabelHidden,
  subText,
  label,
  multiple: isMultiSelect,
  name,
  noOptionsText = 'Nothing to select',
  options,
  groups,
  placeholder,
  required,
  showBlankOption = false,
  tooltipText,
  explanationUrl,
  isReadOnly,
  onChange,
  value,
  isEmpty,
  isErrorTextHidden,
  isCompact,
  isSubtextHidden,
  renderValue,
  renderOption,
  ...props
}: BaseSelectProps<OptionType>) {
  const [idRandomify] = useState(uuidv4);

  // we need to have special rendering for inside the select in order to
  // support a placeholder: either show the label of the selected value(s),
  // or show the placeholder (which defaults to null if no placeholder text is provided)
  const renderValueOrPlaceholder = useCallback(
    (val) => {
      if (isEmpty) return <Placeholder placeholder={placeholder} />;
      return renderValue(val);
    },
    [isEmpty, placeholder, renderValue],
  );

  const groupedOptions = useMemo((): { [key: string]: OptionType[] } => {
    if (!groups) return {};
    const result = groupBy(
      options.filter((option) => option.groupName),
      'groupName',
    );
    // Ensure that there are no options under a group name with no corresponding group defined
    const validGroupNames = new Set(groups.map((group) => group.name));
    Object.keys(result).forEach((groupName) => {
      if (!validGroupNames.has(groupName)) {
        throw new Error(
          `Option group name ${groupName} not found in Select groups array`,
        );
      }
    });
    return result;
  }, [options, groups]);

  const elementId = `${name || label}-${idRandomify}`;
  const labelId = `${elementId}-label`;
  const subTextId = `${elementId}-sub-text`;

  const labelComponent = (
    <SelectLabel id={labelId} required={required}>
      {label || name}
    </SelectLabel>
  );

  const getOption = useCallback(
    (option: OptionType) => (
      <MenuItem
        key={option.value.toString()}
        value={option.value}
        disabled={isReadOnly || option.disabled}
      >
        {renderOption(option)}
      </MenuItem>
    ),
    [isReadOnly, renderOption],
  );

  const getGroupedOptionElements = useCallback(
    (group: SelectGroup) => {
      const groupOptions = groupedOptions[group.name];
      if (!groupOptions?.length) return null;
      return [
        <GroupLabel
          key={`${elementId}-${group.name}-label`}
          data-group-header // used in the handleChange to prevent the label from being selected as a menu item
        >
          {group.label}
        </GroupLabel>,
        group.tooltip && (
          <GroupTooltip
            key={`${elementId}-${group.name}-tooltip`}
            data-group-header // used in the handleChange to prevent the tooltip from being selected as a menu item
            title={group.tooltip}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            placement="bottom-start"
          />
        ),
        ...groupOptions.map((option: OptionType) => getOption(option)),
      ];
    },
    [groupedOptions, elementId, getOption],
  );

  // Don't allow the group subheader + tooltip to be treated like a menu item that can be selected on click
  const handleChange = useCallback(
    (e, child) => {
      if (child?.props?.['data-group-header']) {
        return;
      }
      onChange?.(e, child);
    },
    [onChange],
  );

  return (
    <FormControl fullWidth error={error} className={className}>
      {isLabelHidden ? (
        <VisuallyHidden>{labelComponent}</VisuallyHidden>
      ) : (
        <InputLabelContainer>
          <LabelAndTooltipContainer>
            {labelComponent}
            {tooltipText && <Tooltip title={tooltipText} placement="right" />}
          </LabelAndTooltipContainer>
          {explanationUrl && (
            <ExplanationLink
              href={explanationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              What is this?
            </ExplanationLink>
          )}
        </InputLabelContainer>
      )}
      <SelectContainer
        id={elementId}
        labelId={labelId}
        aria-describedby={subText ? subTextId : undefined}
        // place menu under the select container
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            // leave extra room under the anchor element so it doesn't cover the border
            vertical: -1 * borderWidthInPx,
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        disableUnderline
        displayEmpty
        multiple={isMultiSelect}
        renderValue={renderValueOrPlaceholder}
        required={required}
        value={value}
        onChange={handleChange}
        {...props}
      >
        {showBlankOption && (
          <MenuItem value="" disabled={isReadOnly}>
            &nbsp;
          </MenuItem>
        )}
        {options.filter((option) => !option.groupName).map(getOption)}
        {groups && groups.flatMap(getGroupedOptionElements)}
        {!showBlankOption && !options.length && (
          <NoOptionsText>{noOptionsText}</NoOptionsText>
        )}
      </SelectContainer>
      {subText && !isSubtextHidden && (
        <SubText id={subTextId}>{subText}</SubText>
      )}
      <ConditionalWrapper
        condition={!!isErrorTextHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <ErrorText>
          {errorText || (dontSaveSpaceForErrorText ? '' : ' ')}
        </ErrorText>
      </ConditionalWrapper>
    </FormControl>
  );
}
